import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Trash2, Pencil } from "lucide-react";
import TableContainer from "Common/TableContainer";
import { Link } from "react-router-dom";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import useCurrentRole from "Common/hooks/useCurrentRole";
import { canManageInitialData } from "Common/constants/roles";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { apiErrorMessage } from "Common/constants/apiErrors";

// اعتبارسنجی کد ملی ایران (الگوریتم استاندارد ۱۰ رقمی)
const isValidNationalCode = (code?: string) => {
  if (!code || !/^\d{10}$/.test(code)) return false;
  if (/^(\d)\1{9}$/.test(code)) return false;
  const check = parseInt(code[9], 10);
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(code[i], 10) * (10 - i);
  }
  const remainder = sum % 11;
  return remainder < 2 ? check === remainder : check === 11 - remainder;
};

const Index = () => {
  const navigate = useNavigate();
  // مدیریت مشتری (ایجاد/ویرایش/حذف): ادمین/سوپرادمین و همچنین کارشناس فروش و مدیر
  // فروش که به داده‌های اولیه و همه‌ی عملیات آن دسترسی دارند. کنترل واقعی همچنان با
  // سرور است؛ این فقط برای پنهان‌کردن دکمه‌های غیرمجاز است.
  const { roleId } = useCurrentRole();
  const isAdmin = canManageInitialData(roleId);

  const [data, setData] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">("default");
  const changeServerSort = useCallback((nextSortBy: string | undefined, nextSortOrder: "default" | "asc" | "desc") => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
  }, []);
  const [provinces, setProvinces] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [filteredCities, setFilteredCities] = useState<any[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [eventData, setEventData] = useState<any>(null);
  const [reload, setReload] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  // دریافت لیست استان‌ها
  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get("https://portal-api.mahanpanel.com/provinces/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setProvinces(items);
      } catch (error) {
        console.error("Error fetching provinces:", error);
      }
    };
    fetchProvinces();
  }, []);

  // دریافت لیست شهرها
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get("https://portal-api.mahanpanel.com/cities/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setCities(items);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, []);

  // دریافت لیست مشتریان
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get("https://portal-api.mahanpanel.com/customers/", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder },
        });
        const items = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setData(items);
        setErrorMessage("");
      } catch (error: any) {
        console.error("Error fetching customers:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        setErrorMessage("مشکلی در ارتباط با سرور رخ داده است.");
      }
    };
    fetchData();
  }, [reload, navigate, sortBy, sortOrder]);

  // فرم مشتری (ثبت و ویرایش)
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      customer_type: "individual",
      first_name: "",
      last_name: "",
      national_code: "",
      national_id: "",
      economic_code: "",
      phone_number: "",
      province_id: "",
      city_id: "",
      postal_code: "",
      address: "",
    },
    validationSchema: Yup.object({
      customer_type: Yup.string()
        .oneOf(["individual", "legal"])
        .required("لطفاً نوع مشتری را انتخاب کنید"),
      // مستند نسخه ۴: برای مشتری حقیقی «نام»، «نام خانوادگی» و «شماره همراه» اجباری‌اند
      // و بقیه اختیاری. برای مشتری حقوقی نام شرکت در first_name می‌آید و نام خانوادگی
      // اصلاً ارسال نمی‌شود، پس الزامی هم نیست.
      first_name: Yup.string().trim().required("لطفاً نام را وارد کنید"),
      last_name: Yup.string().when("customer_type", {
        is: "individual",
        then: (schema) => schema.trim().required("لطفاً نام خانوادگی را وارد کنید"),
        otherwise: (schema) => schema.notRequired(),
      }),
      // کد ملی اختیاری است؛ ولی اگر وارد شود باید معتبر باشد تا داده‌ی غلط ثبت نشود.
      national_code: Yup.string()
        .notRequired()
        .test("national-code-format", "کد ملی باید ۱۰ رقم باشد", (value) =>
          !value ? true : /^\d{10}$/.test(value)
        )
        .test("valid-national-code", "کد ملی معتبر نیست", (value) =>
          !value ? true : isValidNationalCode(value)
        ),
      national_id: Yup.string()
        .notRequired()
        .test("national-id-format", "شناسه ملی باید ۱۱ رقم باشد", (value) =>
          !value ? true : /^\d{11}$/.test(value)
        ),
      economic_code: Yup.string()
        .notRequired()
        .test("economic-code-format", "کد اقتصادی حداقل ۸ رقم است", (value) =>
          !value ? true : value.length >= 8
        ),
      phone_number: Yup.string()
        .required("لطفاً شماره تلفن را وارد کنید")
        .matches(/^0\d{10}$/, "شماره تلفن معتبر نیست (مثلاً 09121234567)"),
      // استان و شهر اختیاری‌اند (نسخه ۴). انتخاب فقط یکی هم پذیرفته می‌شود؛ ولی اگر
      // هر دو انتخاب شده باشند باید با هم سازگار باشند.
      province_id: Yup.string().notRequired(),
      city_id: Yup.string()
        .notRequired()
        .test("city-belongs-to-province", "شهر انتخاب‌شده متعلق به استان انتخابی نیست", function (value) {
          const provinceId = this.parent.province_id;
          if (!provinceId || !value) return true;
          const city = cities.find((c) => c.id === Number(value));
          return city?.province_id === Number(provinceId);
        }),
      postal_code: Yup.string()
        .notRequired()
        .test("postal-code-format", "کد پستی باید ۱۰ رقم باشد", (value) =>
          !value ? true : /^\d{10}$/.test(value)
        ),
      address: Yup.string()
        .notRequired()
        .test("address-length", "حداقل ۵ کاراکتر", (value) =>
          !value ? true : value.trim().length >= 5
        ),
    }),
    onSubmit: async (values) => {
      const payload = {
        customer_type: values.customer_type,
        first_name: values.first_name,
        // برای مشتری حقوقی، نام خانوادگی و کد ملی اصلاً ارسال نمی‌شوند و برای حقیقی
        // شناسه ملی و کد اقتصادی ارسال نمی‌شوند (قواعد نسخه ۴).
        last_name: values.customer_type === "individual" ? values.last_name : undefined,
        national_code: values.customer_type === "individual" ? values.national_code : undefined,
        national_id: values.customer_type === "legal" ? values.national_id : undefined,
        economic_code: values.customer_type === "legal" ? values.economic_code : undefined,
        phone_number: values.phone_number,
        // موقعیت اختیاری است: برای انتخاب‌نشده باید null برود، نه صفر و نه رشته‌ی خالی.
        province_id: values.province_id ? Number(values.province_id) : null,
        city_id: values.city_id ? Number(values.city_id) : null,
        postal_code: values.postal_code,
        address: values.address,
      };

      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        let res;

        if (editMode && editingId) {
          // ویرایش مشتری
          res = await axios.patch(
            `https://portal-api.mahanpanel.com/customers/${editingId}`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (res.status === 200) {
            Swal.fire("عملیات موفق", `مشتری "${values.first_name}" بروزرسانی شد`, "success");
            setReload(!reload);
            toggle();
          }
        } else {
          // ثبت مشتری جدید
          res = await axios.post(
            "https://portal-api.mahanpanel.com/customers/",
            payload,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (res.status === 201) {
            Swal.fire("عملیات موفق", `مشتری "${values.first_name}" ثبت شد`, "success");
            setReload(!reload);
            toggle();
          }
        }
      } catch (error: any) {
        console.error("Submit error:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        if (error.response?.status === 403) {
          Swal.fire("عدم دسترسی", "شما اجازه‌ی انجام این عملیات را ندارید (فقط ادمین/سوپرادمین مجاز است)", "error");
        } else if (error.response?.status === 422) {
          // خطای اعتبارسنجی سرور به پیغام فارسی تبدیل می‌شود.
          const msg = apiErrorMessage(error, "اطلاعات وارد شده نامعتبر است");
          Swal.fire("خطا", msg, "error");
        } else {
          Swal.fire("خطا", apiErrorMessage(error, "مشکل در ثبت یا ویرایش مشتری"), "error");
        }
      }
    },
  });

  // فیلتر شهرها بر اساس استان انتخاب‌شده
  useEffect(() => {
    if (validation.values.province_id) {
      const filtered = cities.filter(
        (city) => city.province_id === Number(validation.values.province_id)
      );
      setFilteredCities(filtered);
    } else {
      setFilteredCities([]);
    }
  }, [validation.values.province_id, cities]);

  // حذف مشتری
  const onClickDelete = (row: any) => {
    setDeleteModal(true);
    setEventData(row);
  };

  const handleDelete = async () => {
    if (!eventData) return;
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.delete(
        `https://portal-api.mahanpanel.com/customers/${eventData.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "مشتری با موفقیت حذف شد", "success");
        setData((prev) => prev.filter((item) => item.id !== eventData.id));
        setDeleteModal(false);
        setEventData(null);
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      if (error.response?.status === 403) {
        Swal.fire("عدم دسترسی", "شما اجازه‌ی انجام این عملیات را ندارید (فقط ادمین/سوپرادمین مجاز است)", "error");
      } else {
        Swal.fire("عملیات ناموفق", "خطا در حذف مشتری", "error");
      }
      setDeleteModal(false);
      setEventData(null);
    }
  };

  // باز کردن مودال برای ویرایش
  const handleEdit = (row: any) => {
    setEditMode(true);
    setEditingId(row.id);
    validation.setValues({
      customer_type: row.customer_type || "individual",
      first_name: row.first_name || "",
      last_name: row.last_name || "",
      national_code: row.national_code || "",
      national_id: row.national_id || "",
      economic_code: row.economic_code || "",
      phone_number: row.phone_number || "",
      province_id: row.province_id ? String(row.province_id) : "",
      city_id: row.city_id ? String(row.city_id) : "",
      postal_code: row.postal_code || "",
      address: row.address || "",
    });
    setShow(true);
  };

  // باز/بستن مودال
  const toggle = useCallback(() => {
    setShow((prev) => !prev);
    if (show) {
      // بستن مودال
      setEditMode(false);
      setEditingId(null);
      validation.resetForm();
      setEventData(null);
      setFilteredCities([]);
    } else {
      // باز کردن مودال (برای ثبت جدید)
      setEditMode(false);
      setEditingId(null);
      validation.resetForm();
      setEventData(null);
    }
  }, [show, validation]);

  const handleSubmitButton = (e: React.MouseEvent) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  const handleCustomerTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const type = e.target.value;
    validation.setFieldValue("customer_type", type);
    if (type === "individual") {
      validation.setFieldValue("national_id", "");
      validation.setFieldValue("economic_code", "");
    } else {
      validation.setFieldValue("national_code", "");
      validation.setFieldValue("last_name", "");
    }
  };

  const handleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const provinceId = e.target.value;
    validation.setFieldValue("province_id", provinceId);
    validation.setFieldValue("city_id", "");

    // بررسی اینکه آیا استان انتخابی شهر دارد یا خیر
    if (provinceId) {
      const hasCities = cities.some((city) => city.province_id === Number(provinceId));
      if (!hasCities) {
        Swal.fire({
          icon: "info",
          title: "توجه",
          text: "این استان در حال حاضر هیچ شهری ندارد. لطفاً ابتدا شهرهای آن را ثبت کنید.",
          confirmButtonText: "متوجه شدم",
        });
      }
    }
  };

  const isIndividual = validation.values.customer_type === "individual";
  const isLegal = validation.values.customer_type === "legal";

  // ستون‌های جدول
  const columns = useMemo(
    () => [
      {
        header: "شناسه",
        accessorKey: "id",
        cell: (cell: any) => <span>{cell.getValue()}</span>,
      },
      {
        header: "نوع مشتری",
        accessorKey: "customer_type",
        cell: (cell: any) =>
          cell.getValue() === "individual" ? "حقیقی" : "حقوقی",
      },
      {
        header: "نام",
        accessorKey: "first_name",
        meta: { serverSortBy: "full_name" },
        cell: (cell: any) => (
          <Link
            to="#!"
            className="transition-all duration-150 ease-linear text-custom-500 hover:text-custom-600"
          >
            {cell.getValue()}
          </Link>
        ),
      },
      {
        header: "نام خانوادگی",
        accessorKey: "last_name",
        cell: (cell: any) => cell.getValue() || "-",
      },
      {
        header: "کد ملی",
        accessorKey: "national_code",
        cell: (cell: any) => cell.getValue() || "-",
      },
      {
        header: "تلفن",
        accessorKey: "phone_number",
      },
      // استان و شهر اختیاری‌اند و ممکن است null باشند؛ «—» نمایش داده می‌شود.
      {
        header: "استان",
        accessorKey: "province_name",
        cell: (cell: any) => cell.getValue() || "—",
      },
      {
        header: "شهر",
        accessorKey: "city_name",
        cell: (cell: any) => cell.getValue() || "—",
      },
      {
        header: "عملیات",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => {
          if (!isAdmin) return <span className="text-slate-400 text-xs">—</span>;
          return (
            <div className="flex gap-3">
              <Link to="#!" onClick={() => handleEdit(cell.row.original)}>
                <Pencil className="size-4 text-blue-500 hover:text-blue-700" />
              </Link>
              <Link to="#!" onClick={() => onClickDelete(cell.row.original)}>
                <Trash2 className="size-4 text-red-500 hover:text-red-700" />
              </Link>
            </div>
          );
        },
      },
    ],
    [isAdmin]
  );

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="ثبت مشتری" title="داده‌های اولیه" />
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
      {errorMessage && (
        <div className="text-center text-red-500 mb-3">{errorMessage}</div>
      )}
      <div className="card" id="employeeTable rtl">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              مجموع مشتریان (<b className="total-Employs">{data.length}</b>)
            </h6>
            <div className="shrink-0">
              <Link
                to="#!"
                onClick={() => {
                  setEditMode(false);
                  setEditingId(null);
                  validation.resetForm();
                  setShow(true);
                }}
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600"
              >
                <span className="align-middle rtl">ثبت مشتری جدید</span>{" "}
                <Plus className="inline-block size-4 rtl" />
              </Link>
            </div>
          </div>
          {data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={data}
              serverSorting={{ sortBy, sortOrder, onChange: changeServerSort }}
              customPageSize={10}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter
              count={errorMessage ? 1 : 15}
              text={errorMessage || "داده‌ای در جدول وجود ندارد"}
            />
          )}
        </div>
      </div>

      {/* مودال ثبت/ویرایش مشتری */}
      <Modal
        show={show}
        onHide={toggle}
        modal-center="true"
        dialogClassName="w-screen md:w-[36rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">
            {editMode ? "ویرایش مشتری" : "اضافه کردن مشتری"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form
            className="create-form"
            id="create-form"
            onSubmit={validation.handleSubmit}
          >
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {/* نوع مشتری */}
              <div className="dark:text-white text-gray-800">
                <label htmlFor="customer_type" className="block text-base font-medium mb-1">
                  نوع مشتری:
                </label>
                <select
                  id="customer_type"
                  className="form-select font-[12px] w-full"
                  name="customer_type"
                  onChange={handleCustomerTypeChange}
                  value={validation.values.customer_type}
                >
                  <option value="individual">حقیقی</option>
                  <option value="legal">حقوقی</option>
                </select>
                {validation.touched.customer_type && validation.errors.customer_type && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.customer_type === "string"
                      ? validation.errors.customer_type
                      : ""}
                  </p>
                )}
              </div>

              {/* نام */}
              <div className="dark:text-white text-gray-800">
                <label htmlFor="first_name" className="block text-base font-medium mb-1">
                  نام: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  className="form-input font-[12px] w-full"
                  name="first_name"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.first_name}
                  placeholder="نام"
                />
                {validation.touched.first_name && validation.errors.first_name && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.first_name === "string"
                      ? validation.errors.first_name
                      : ""}
                  </p>
                )}
              </div>

              {/* نام خانوادگی */}
              <div className="dark:text-white text-gray-800">
                <label htmlFor="last_name" className="block text-base font-medium mb-1">
                  نام خانوادگی:
                  {!isLegal && <span className="text-red-500"> *</span>}
                </label>
                <input
                  type="text"
                  id="last_name"
                  className="form-input font-[12px] w-full disabled:bg-slate-100 dark:disabled:bg-zink-500"
                  name="last_name"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.last_name}
                  placeholder="نام خانوادگی"
                />
                {validation.touched.last_name && validation.errors.last_name && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.last_name === "string"
                      ? validation.errors.last_name
                      : ""}
                  </p>
                )}
              </div>

              {/* کد ملی */}
              <div className="dark:text-white text-gray-800">
                <label htmlFor="national_code" className="block text-base font-medium mb-1">
                  کد ملی:
                </label>
                <input
                  type="text"
                  id="national_code"
                  disabled={isLegal}
                  maxLength={10}
                  className="form-input font-[12px] w-full disabled:bg-slate-100 dark:disabled:bg-zink-500"
                  name="national_code"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.national_code}
                  placeholder="۱۰ رقم"
                />
                {validation.touched.national_code && validation.errors.national_code && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.national_code === "string"
                      ? validation.errors.national_code
                      : ""}
                  </p>
                )}
              </div>

              {/* شناسه ملی */}
              <div className="dark:text-white text-gray-800">
                <label htmlFor="national_id" className="block text-base font-medium mb-1">
                  شناسه ملی:
                </label>
                <input
                  type="text"
                  id="national_id"
                  disabled={isIndividual}
                  maxLength={11}
                  className="form-input font-[12px] w-full disabled:bg-slate-100 dark:disabled:bg-zink-500"
                  name="national_id"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.national_id}
                  placeholder="۱۱ رقم"
                />
                {validation.touched.national_id && validation.errors.national_id && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.national_id === "string"
                      ? validation.errors.national_id
                      : ""}
                  </p>
                )}
              </div>

              {/* کد اقتصادی */}
              <div className="dark:text-white text-gray-800">
                <label htmlFor="economic_code" className="block text-base font-medium mb-1">
                  کد اقتصادی:
                </label>
                <input
                  type="text"
                  id="economic_code"
                  disabled={isIndividual}
                  className="form-input font-[12px] w-full disabled:bg-slate-100 dark:disabled:bg-zink-500"
                  name="economic_code"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.economic_code}
                  placeholder="کد اقتصادی"
                />
                {validation.touched.economic_code && validation.errors.economic_code && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.economic_code === "string"
                      ? validation.errors.economic_code
                      : ""}
                  </p>
                )}
              </div>

              {/* تلفن */}
              <div className="dark:text-white text-gray-800">
                <label htmlFor="phone_number" className="block text-base font-medium mb-1">
                  تلفن: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="phone_number"
                  className="form-input font-[12px] w-full"
                  name="phone_number"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.phone_number}
                  placeholder="مثلاً 09121234567"
                />
                {validation.touched.phone_number && validation.errors.phone_number && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.phone_number === "string"
                      ? validation.errors.phone_number
                      : ""}
                  </p>
                )}
              </div>

              {/* استان */}
              <div className="dark:text-white text-gray-800">
                <label htmlFor="province_id" className="block text-base font-medium mb-1">
                  استان:
                </label>
                <select
                  id="province_id"
                  className="form-select font-[12px] w-full"
                  name="province_id"
                  onChange={handleProvinceChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.province_id}
                >
                  <option value="">انتخاب استان</option>
                  {provinces.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {validation.touched.province_id && validation.errors.province_id && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.province_id === "string"
                      ? validation.errors.province_id
                      : ""}
                  </p>
                )}
              </div>

              {/* شهر */}
              <div className="dark:text-white text-gray-800">
                <label htmlFor="city_id" className="block text-base font-medium mb-1">
                  شهر:
                </label>
                <select
                  id="city_id"
                  className="form-select font-[12px] w-full"
                  name="city_id"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.city_id}
                  disabled={!validation.values.province_id}
                >
                  <option value="">انتخاب شهر</option>
                  {filteredCities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {validation.touched.city_id && validation.errors.city_id && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.city_id === "string"
                      ? validation.errors.city_id
                      : ""}
                  </p>
                )}
                {validation.values.province_id && filteredCities.length === 0 && (
                  <p className="text-orange-500 mt-1 text-sm">
                    ⚠️ این استان در حال حاضر هیچ شهری ندارد. ابتدا شهر ثبت کنید.
                  </p>
                )}
              </div>

              {/* کد پستی */}
              <div className="dark:text-white text-gray-800">
                <label htmlFor="postal_code" className="block text-base font-medium mb-1">
                  کد پستی:
                </label>
                <input
                  type="text"
                  id="postal_code"
                  className="form-input font-[12px] w-full"
                  name="postal_code"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.postal_code}
                  placeholder="۱۰ رقم"
                />
                {validation.touched.postal_code && validation.errors.postal_code && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.postal_code === "string"
                      ? validation.errors.postal_code
                      : ""}
                  </p>
                )}
              </div>

              {/* آدرس */}
              <div className="dark:text-white text-gray-800 xl:col-span-2">
                <label htmlFor="address" className="block text-base font-medium mb-1">
                  آدرس:
                </label>
                <input
                  type="text"
                  id="address"
                  className="form-input font-[12px] w-full"
                  name="address"
                  autoComplete="off"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.address}
                  placeholder="آدرس کامل"
                />
                {validation.touched.address && validation.errors.address && (
                  <p className="text-red-400 mt-1 text-sm">
                    {typeof validation.errors.address === "string"
                      ? validation.errors.address
                      : ""}
                  </p>
                )}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={handleSubmitButton}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600 hover:border-custom-600 focus:ring focus:ring-custom-100 dark:focus:ring-custom-400/20"
          >
            {editMode ? "ویرایش" : "ثبت"}
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Index;
